// #region Imports
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CancelIcon from "@mui/icons-material/Cancel";
import LogoutIcon from "@mui/icons-material/Logout";
import EditSquareIcon from "@mui/icons-material/EditSquare";
import MessageIcon from "@mui/icons-material/Message";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { styled } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import { getForumsByTopic, populateForumsByTopic } from "messaging/forums/_stores/forums-redux-slice";
import Topic from "messaging/forums/topics/topic";
import ForumEntityClass from "notifications/values/forum-entity-class/forum-entity-class";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { MaskedTeam } from "work/_shared/masked-fields";
import { Content, Header, TabContent, TabsContainer } from "work/_views/work-page-styled-components";
import {
  useEndWorkAgreementMutation,
  useGetWorkAgreementByIdQuery,
  useQuitWorkTeamMutation,
  useReviseWorkAgreementMutation,
} from "work/work-agreements/_api/work-agreement-api";
import { getWorkAgreementById, isWorkAgreementLoadIssue } from "work/work-agreements/_store/work-agreement-redux-slice";
import { PageTab } from "work/work-agreements/_views/view/view-work-agreement-page-tabs";
import ConflictsTab from "work/work-agreements/_views/view/tabs/view-work-conflicts-tab";
import DetailsTab from "work/work-agreements/_views/view/tabs/view-work-details-tab";
import DiscountTab from "work/work-agreements/_views/view/tabs/view-work-discount-tab";
import FeeScheduleTab from "work/work-agreements/_views/view/tabs/view-work-fee-schedule-tab";
import PoliciesTab from "work/work-agreements/_views/view/tabs/view-work-policies-tab";
import TeamTab from "work/work-agreements/_views/view/tabs/view-work-team-tab";
import WorkAgreement, { WorkAgreementFieldCategory } from "work/work-agreements/work-agreement";
import ChatDrawer from "work/work-agreements/_views/chat-drawer";
import Tooltip from "@mui/material/Tooltip";
import LoadingButton from "_shared/_views/loading/loading-button";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
// #endregion

// #region Styled Components
const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}));
const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));
const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "showMessageDrawer",
})<{
  open?: boolean;
  showMessageDrawer?: boolean;
}>(({ theme, showMessageDrawer }) => ({
  display: "flex",
  height: "100%",
  flexDirection: "column",
  flex: 1,
  padding: theme.spacing(3),
  paddingLeft: 0,
  paddingRight: showMessageDrawer ? 0 : theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: showMessageDrawer ? "-400px" : 0,
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: "relative",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
      },
    },
  ],
}));
const ButtonContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
}));
const MessageButton = styled(IconButton)(() => ({
  paddingBottom: 0,
}));
const MessageButtonLabel = styled(Typography)(() => ({
  fontSize: "0.6em",
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));
const ActionsContainer = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingBottom: theme.spacing(1),
    paddingRight: 0,
    paddingTop: theme.spacing(1),
  },
  backgroundColor: theme.palette.background.default,
  bottom: "0px",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));
const WorkAgreementActions = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexWrap: "nowrap",
  },
  alignContent: "end",
  alignItems: "center",
  display: "grid",
  flex: 1,
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  width: "100%",
  "& > span": {
    width: "100%",
  },
}));
const WorkAgreementActionSpan = styled("span")(({ theme }) => ({
  minWidth: theme.spacing(24),
  width: "100%",
}));
const WorkAgreementActionButton = styled(LoadingButton)(({ theme }) => ({
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  width: "100%",
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));
// #endregion

export default function ActiveWorkAgreementPage() {
  // #region Hooks
  const session = useSession();
  const confirm = useConfirmDialog();
  const dispatch = useAttorneyHubDispatch();
  const { id: idParam, pageTab: pageTabParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Work Agreement id is required
  if (!idParam) throw new Response("Not Found", { status: 404 });

  // #region State
  const [messagesPanelIsOpen, setMessagesPanelIsOpen] = useState<boolean>(true);
  const [isEnding, setIsEnding] = useState(false);
  const [isRevising, setIsRevising] = useState(false);
  const [isLeavingTeam, setIsLeavingTeam] = useState(false);
  // #endregion

  // #region Global Variables
  const workAgreementId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const activeTab = useMemo(() => {
    const tab = pageTabParam as PageTab | undefined;
    return tab ?? PageTab.Details;
  }, [pageTabParam]);

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  // #region Work Agreement Queries
  const workAgreementJson = useSelector(getWorkAgreementById(workAgreementId));
  const workAgreement = useMemo(() => {
    if (!workAgreementJson || isWorkAgreementLoadIssue(workAgreementJson)) return null;
    return WorkAgreement.fromJSON(workAgreementJson);
  }, [workAgreementJson]);

  const workAgreementQuery = useGetWorkAgreementByIdQuery(
    !workAgreementId || workAgreementId.isEmpty
      ? skipToken
      : { workAgreementId: workAgreementId?.value ?? Guid.EMPTY.value, session },
  );

  // #region Work Agreement Mutations
  const [endWorkAgreement] = useEndWorkAgreementMutation();
  const [reviseWorkAgreement] = useReviseWorkAgreementMutation();
  const [quitWorkTeam] = useQuitWorkTeamMutation();

  // #region Messages Queries
  const topic = useMemo(() => {
    return Topic.create({
      entityClass: new ForumEntityClass("Work.Agreement"),
      entityId: workAgreementId,
    });
  }, [workAgreementId]);
  const forums = useSelector(getForumsByTopic(topic));

  // #region Global State
  const isLoading = workAgreementQuery.isLoading || sessionPermissionsLoading;
  const isArchived = useMemo(() => {
    return location.pathname.includes("/archived/");
  }, [location.pathname]);

  // #region Lifecycle
  useEffect(() => {
    // Work Agreement invalid/not found
    const shouldShowNotFound =
      Boolean(workAgreementId) && !workAgreementId?.isEmpty && workAgreement === null && !workAgreementQuery.isLoading;

    if (shouldShowNotFound && idParam) {
      enqueueSnackbar("Work Agreement not found", { variant: "warning" });
      navigate(`/work/${isArchived ? "archived" : "active"}`, { replace: true });
    }
  }, [activeTab, idParam, navigate, workAgreement, workAgreementId, workAgreementQuery.isLoading, isArchived]);

  // Permissions bootstrap
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);
  // #endregion

  // #region Functions
  function handleToggleMessagesPanel() {
    setMessagesPanelIsOpen((prev) => !prev);
  }

  // Message threads bootstrap
  useEffect(() => {
    if (!workAgreement || workAgreementQuery.isLoading) return;
    dispatch(populateForumsByTopic({ session, topic }));
  }, [dispatch, session, topic, workAgreement, workAgreementId, workAgreementQuery.isLoading]);

  // Account view switching
  useEffect(() => {
    if (!workAgreement) return;
    if (
      MaskedTeam.isPresentNotMasked(workAgreement.vendorTeam) &&
      MaskedTeam.isPresentNotMasked(workAgreement.clientTeam) &&
      session.context?.viewingAsVendor &&
      workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id)
    ) {
      session.setAccountViewType(AccountType.Client);
    }

    // If viewing as client but user is vendor team member or leader, switch to vendor view
    else if (
      MaskedTeam.isPresentNotMasked(workAgreement.vendorTeam) &&
      !session.context?.viewingAsVendor &&
      (workAgreement.vendorTeam?.leader?.userId.isEqualTo(session.user?.id) ||
        workAgreement.vendorTeam?.members?.some((member) => member.userId.isEqualTo(session.user?.id)))
    ) {
      session.setAccountViewType(AccountType.Vendor);
    }
  }, [workAgreement, workAgreement?.id?.value, session]);

  function handleTabChange(_event: ChangeEvent<object> | null, newTab: PageTab) {
    navigate(`/work/${isArchived ? "archived" : "active"}/view/${workAgreementId?.value}/${newTab}`);
  }

  const handleNavigateBack = useCallback(() => {
    navigate(`/work/${isArchived ? "archived" : "active"}`, { replace: true });
  }, [navigate, isArchived]);

  async function handleEndWorkAgreement() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }

    const response = await confirm({
      title: "End Work Agreement?",
      message:
        "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "End",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsEnding(true);

      await endWorkAgreement({ workAgreementId: workAgreement.id.value }).unwrap();
      enqueueSnackbar("Work agreement ended", { variant: "success" });
      navigate(`/work/archived/view/${workAgreement.id.value}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to end work agreement", { variant: "error" });
    } finally {
      setIsEnding(false);
    }
  }

  async function handleReviseWorkAgreement() {
    if (!workAgreement?.id) {
      throw new Error("Work agreement not found");
    }
    if (!session.user) {
      throw new Error("User not found");
    }

    const response = await confirm({
      title: "Revise Work Agreement?",
      message:
        "This will void the work agreement for any future work and only work performed " +
        "before this date will be billable until a new proposal is agreed to.",
      okButtonText: "Revise",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsRevising(true);
      const proposalJson = await reviseWorkAgreement({ workAgreement, session }).unwrap();
      if (!proposalJson.id) {
        throw new Error("Invalid proposal response");
      }
      enqueueSnackbar("New revision created", { variant: "success" });
      navigate(`/proposals/active/revise/${proposalJson.id}/details`, { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to revise work agreement", { variant: "error" });
    } finally {
      setIsRevising(false);
    }
  }

  async function handleLeaveTeam() {
    if (!workAgreement?.id) {
      console.error("Work agreement not found");
      return;
    }
    if (!session.user?.id) {
      console.error("User not found");
      return;
    }

    const response = await confirm({
      title: "Leave Team?",
      message: "Are you sure you want to leave the vendor-team?",
      okButtonText: "Leave",
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      setIsLeavingTeam(true);
      await quitWorkTeam({ workId: workAgreement.id.value }).unwrap();
      enqueueSnackbar("You have left the vendor-team", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to leave vendor-team", { variant: "error" });
    } finally {
      setIsLeavingTeam(false);
    }
  }

  // #region Render
  if (!sessionPermissions || isLoading)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToWork) return <NotAuthorized />;

  return (
    <PageContainer>
      <Main open={messagesPanelIsOpen} showMessageDrawer={!!workAgreement}>
        <HeaderContainer>
          <TitleContainer>
            <Chip
              icon={<ArrowBackIcon />}
              size="medium"
              label="Back"
              onClick={handleNavigateBack}
              color="primary"
              sx={{ mr: 2 }}
            />
            <Typography variant="h4">
              <span style={{ color: "gray", marginRight: "8px" }}>Work Agreements &gt;</span>
              {workAgreement?.name?.value ?? "Loading..."}
            </Typography>
          </TitleContainer>
          <ButtonContainer>
            <MessageButton
              size="medium"
              color="primary"
              disabled={!workAgreement?.id || !workAgreement}
              onClick={() => handleToggleMessagesPanel()}
            >
              <MessageIcon fontSize="medium" />
            </MessageButton>
            <MessageButtonLabel variant="button" color={!workAgreement?.id || !workAgreement ? "darkgray" : "primary"}>
              Messages
            </MessageButtonLabel>
          </ButtonContainer>
        </HeaderContainer>
        <Header>
          <TabsContainer
            variant="scrollable"
            scrollButtons="auto"
            indicatorColor="primary"
            textColor="primary"
            value={activeTab}
            onChange={handleTabChange}
          >
            <Tab value={PageTab.Details} label={WorkAgreementFieldCategory.Details} />
            <Tab value={PageTab.Team} label={WorkAgreementFieldCategory.Team} />
            <Tab value={PageTab.FeeSchedule} label={WorkAgreementFieldCategory.FeeSchedule} />
            <Tab value={PageTab.Conflicts} label={WorkAgreementFieldCategory.Conflicts} />
            <Tab value={PageTab.Policies} label={WorkAgreementFieldCategory.Policies} />
            <Tab value={PageTab.Discount} label={WorkAgreementFieldCategory.Discount} />
          </TabsContainer>
        </Header>
        {!isArchived && (
          <ActionsContainer>
            <WorkAgreementActions>
              {((MaskedTeam.isPresentNotMasked(workAgreement?.clientTeam) &&
                workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id)) ||
                (MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
                  workAgreement?.vendorTeam?.leader?.userId.isEqualTo(session.user?.id))) && (
                <Tooltip
                  enterDelay={600}
                  placement="bottom-start"
                  title="Revise the work agreement to make changes to terms or team members. 
                  This will end the current agreement and create a new proposal for review."
                >
                  <WorkAgreementActionSpan>
                    <WorkAgreementActionButton
                      variant="outlined"
                      color="primary"
                      startIcon={<EditSquareIcon />}
                      loading={isRevising}
                      disabled={isEnding || isRevising || isLeavingTeam}
                      onClick={handleReviseWorkAgreement}
                    >
                      Revise
                    </WorkAgreementActionButton>
                  </WorkAgreementActionSpan>
                </Tooltip>
              )}
              {MaskedTeam.isPresentNotMasked(workAgreement?.vendorTeam) &&
                workAgreement?.vendorTeam?.members.some((member) => member.userId.isEqualTo(session.user?.id)) && (
                  <Tooltip
                    title="Leave the vendor team. All work done after leaving will not be tracked."
                    placement="bottom"
                  >
                    <WorkAgreementActionButton
                      variant="contained"
                      color="primary"
                      startIcon={<LogoutIcon />}
                      loading={isLeavingTeam}
                      disabled={isLeavingTeam || isEnding || isRevising}
                      onClick={handleLeaveTeam}
                    >
                      Leave Team
                    </WorkAgreementActionButton>
                  </Tooltip>
                )}
              {MaskedTeam.isPresentNotMasked(workAgreement?.clientTeam) &&
                workAgreement.clientTeam.leader?.userId.isEqualTo(session.user?.id) && (
                  <Tooltip title="End Work Agreement" placement="bottom">
                    <WorkAgreementActionSpan>
                      <WorkAgreementActionButton
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        loading={isEnding}
                        disabled={isEnding || isRevising || isLeavingTeam}
                        onClick={handleEndWorkAgreement}
                      >
                        End Agreement
                      </WorkAgreementActionButton>
                    </WorkAgreementActionSpan>
                  </Tooltip>
                )}
            </WorkAgreementActions>
          </ActionsContainer>
        )}
        <Content>
          <TabContent>
            {workAgreement && (
              <>
                {activeTab === PageTab.Details && <DetailsTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Team && (
                  <TeamTab entityId={session.user!.legalEntityId} workAgreement={workAgreement} />
                )}
                {activeTab === PageTab.FeeSchedule && <FeeScheduleTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Conflicts && <ConflictsTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Policies && <PoliciesTab workAgreement={workAgreement} />}
                {activeTab === PageTab.Discount && <DiscountTab workAgreement={workAgreement} />}
              </>
            )}
          </TabContent>
        </Content>
      </Main>
      <ChatDrawer
        forums={forums}
        isOpen={messagesPanelIsOpen}
        readOnly={isArchived}
        onClose={() => setMessagesPanelIsOpen(false)}
      />
    </PageContainer>
  );
}
